// -----------------------------------------------------------------Imports---
import { Button, Grid, Paper, TextField, Typography } from "@mui/material";
import { Key, useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { DeliveryAddressGridKey, DeliveryAddressModel, gridKeys } from "../../models/DeliveryAddressModel";
import { PartnerModel } from "../../models/PartnerModel";
import ResponseModel from "../../models/ResponseModel";
import DeliveryAddressService from "../../services/deliveryAddresses/DeliveryAddressService";
import { hasApiError } from "../../utils/Utils";

import { style } from "./AddDeliveryAddress.style";

type AddDeliveryAddressProps = {
  partnerForDelivery: PartnerModel | null;
  id: string | number | undefined;
  setDeliverySetterOpen: React.Dispatch<React.SetStateAction<boolean>>;
  getPartners: () => Promise<void>;
};

// -----------------------------------------------------------------Exports---
const AddDeliveryAddress = (props: AddDeliveryAddressProps): JSX.Element => {
  const [deliveryAddress, setDeliveryAddress] = useState<DeliveryAddressModel>({} as DeliveryAddressModel);
  const [id] = useState<string | number | undefined>(props.id);
  const [partnerForDelivery] = useState<PartnerModel | null>(props.partnerForDelivery ?? null);

  //get a deliveryAddress to update, if id is available
  const getDeliveryAddress = useCallback(async (addrId: string): Promise<void> => {
    const response: ResponseModel = await DeliveryAddressService.getDeliveryAddress(addrId);

    if (hasApiError(response)) return;
    else {
      setDeliveryAddress({ ...response.data } as DeliveryAddressModel);
    }
  }, []);

  //saving the new deliveryAddress
  const saveDeliveryAddress = async (): Promise<void> => {
    //guards
    if (deliveryAddress.name === undefined || deliveryAddress.name.length < 3) {
      toast.error("A név legalább 3 karakter kell legyen");
      return;
    }

    //sending the request
    const loadingToastId = toast.loading("Mentés folyamatban...");
    let response: ResponseModel;
    if (id === undefined)
      response = await DeliveryAddressService.addDeliveryAddress({
        ...deliveryAddress,
        partnerCode: partnerForDelivery?.partnerCode,
      });
    else response = await DeliveryAddressService.updateDeliveryAddress(deliveryAddress, id.toString());

    //showing the result
    if (hasApiError(response)) {
      toast.dismiss(loadingToastId);
      return;
    } else {
      const msg = id === undefined ? "Sikeres szállítási cím rögzítés" : "Sikeres szállítási cím frissítés";
      toast.update(loadingToastId, { render: msg, type: "success", isLoading: false, autoClose: 4000 });
      props.setDeliverySetterOpen(false);
      void props.getPartners();
    }
  };

  //updating the new deliveryAddress state
  const onTextChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, key: string): void => {
    const newDeliveryAddress: DeliveryAddressModel = { ...deliveryAddress };
    (newDeliveryAddress as Record<string, unknown>)[key] = e.target.value;
    setDeliveryAddress(newDeliveryAddress);
  };

  // loading in the partners address for the 1st delivery address
  const onLoadDefault = (): void => {
    const _deliveryAddress: DeliveryAddressModel = {
      name: deliveryAddress?.name,
      postalCode: partnerForDelivery?.postalCode,
      city: partnerForDelivery?.city,
      streetName: partnerForDelivery?.streetName,
      streetType: partnerForDelivery?.streetType,
      houseNumber: partnerForDelivery?.houseNumber,
      building: partnerForDelivery?.building,
      staircase: partnerForDelivery?.staircase,
      floor: partnerForDelivery?.floor,
      door: partnerForDelivery?.door,
      parcelNumber: partnerForDelivery?.parcelNumber,
    } as DeliveryAddressModel;
    setDeliveryAddress(_deliveryAddress);
  };

  //devnote: somehow this is enough
  useEffect((): void => {
    if (id !== undefined) {
      void getDeliveryAddress(id.toString());
    }
  }, [id, getDeliveryAddress]);

  // --------------------------------------------------------------Return---
  return (
    <Paper style={style.paper}>
      {/* Title */}
      <Typography variant="h3" style={style.title}>
        {id === undefined && "Szállítási cím hozzáadása"}
        {id !== undefined && "Szállítási cím frissítése"}
      </Typography>

      {/* Inputs */}
      <Grid container spacing={4}>
        {gridKeys
          .filter((gridKey: DeliveryAddressGridKey): boolean => gridKey.inputHidden !== true)
          .map((gridKey: DeliveryAddressGridKey, i: Key): JSX.Element => {
            return (
              <Grid item key={i} md={gridKey.md ?? 3} sm={gridKey.sm ?? 6} xs={gridKey.xs ?? 12}>
                <Typography style={style.gridLabel} align={gridKey.align ?? "left"}>
                  {gridKey.label}
                </Typography>
                <TextField
                  value={(deliveryAddress as Record<string, unknown>)[gridKey.key] ?? ""}
                  variant="standard"
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void =>
                    onTextChange(e, gridKey.key)
                  }
                />
              </Grid>
            );
          })}
      </Grid>

      {/* Footer buttons */}
      <Button color="success" onClick={onLoadDefault} variant="contained" style={style.saveButton}>
        Alapértelmezett cím betöltése
      </Button>
      <Button
        onClick={(): void => {
          void saveDeliveryAddress();
        }}
        variant="contained"
        style={style.saveButton}
      >
        {id === undefined && "Rögzítés"}
        {id !== undefined && "Frissítés"}
      </Button>
      <Button
        color="error"
        onClick={(): void => props.setDeliverySetterOpen(false)}
        variant="contained"
        style={style.saveButton}
      >
        Mégse
      </Button>
    </Paper>
  );
};

export default AddDeliveryAddress;
